import useHeader from 'containers/App/Header/useHeader';
import { get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { MainBodyStyle } from './style';
import DatePickerComponent from './components/DatePicker';
import DropDownComponent from './components/Dropdown';
import SearchComponent from './components/Search';
import ModalComponent from './components/Filter Modal';
import {
  useFetchCreativeIdListQuery,
  useFetchMetadataQuery,
} from 'services/api/creativePreview';
import { useGetConfigQuery } from 'services/api';
import useQuery from 'reusable/customHooks/useQuery';
import Lottie from 'react-lottie';
import { HomeBadgeComponent } from './components/Badge/homeBadgeComponent';
import { Card } from './components/Card';
import noDataAnimation from './animationAssets/Animation - 1745055233492.json';
import Spinners from './components/Spinner';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import InfiniteScroll from 'react-infinite-scroll-component';
import LastFetched from './components/LastFetched';
import PaginationComponent from './components/Pagination';
import { DefaultPage, DefaultPageSize, FieldMapping } from './constants';
import TestTypeTabs from './components/Tabs';
import Header from './components/Header';
import Forbidden403 from './components/permission';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Button } from '@alphonsocode/kallos';
import OnDemandButton from './components/onDemandButton';
import { track } from 'analytics/posthog';
import {
  BatchSummaryCard,
  getBatchCaseId,
  getBatchLabel,
  getBatchParentId,
  type BatchListGroup,
} from './components/BatchSummaryCard';

type PreviewListItem =
  | {
      type: 'test';
      item: any;
      index: number;
    }
  | {
      type: 'batch';
      group: BatchListGroup;
      index: number;
    };

/** Mosaic URLs include alphCrid and/or alphCid; expand artifacts only for rows that match. */
function rowMatchesMosaicUrlIds(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  row: any,
  alphCrid: string | null,
  alphCid: string | null,
) {
  const rowCrid =
    get(row, FieldMapping.acrids) ?? get(row, 'testMetadata.alph_crid');
  const rowCid = get(row, FieldMapping.acid);
  const sameId = (cell: unknown, param: string | null) => {
    if (param === null || param === '') return true;
    const nParam = Number(param);
    const nCell = Number(cell);
    if (!Number.isNaN(nParam) && !Number.isNaN(nCell)) return nParam === nCell;
    return String(cell ?? '') === String(param);
  };
  if (alphCrid !== null && alphCrid !== '' && !sameId(rowCrid, alphCrid)) {
    return false;
  }
  if (alphCid !== null && alphCid !== '' && !sameId(rowCid, alphCid)) {
    return false;
  }
  return Boolean(
    (alphCrid !== null && alphCrid !== '') ||
      (alphCid !== null && alphCid !== ''),
  );
}

function getDefaultStatusForTestType(
  testType: string,
  mosaicRedirect: boolean,
) {
  if (mosaicRedirect) {
    return 'Rendering Verified,Rendering Failed,CTA Verified,CTA Failed,Test Completed,Unsupported Test,Not Picked,In Progress,Error';
  }

  if (testType === 'ad-render') {
    return 'Rendering Verified';
  }

  if (testType === 'on-demand') {
    return 'Rendering Verified,CTA Verified,Test Completed';
  }

  if (testType === 'ssp') {
    return 'CTA Failed,Rendering Failed';
  }

  return 'Rendering Verified,CTA Verified';
}

function getFilterTabVisibility(filter: any) {
  return filter?.tabs || filter?.tabVisibility || filter?.visibility || null;
}

function isFilterVisibleForTestType(filter: any, testType: string) {
  const tabVisibility = getFilterTabVisibility(filter);
  const include = Array.isArray(tabVisibility?.include)
    ? tabVisibility.include
    : [];
  const exclude = Array.isArray(tabVisibility?.exclude)
    ? tabVisibility.exclude
    : [];

  if (include.length > 0 && !include.includes(testType)) {
    return false;
  }

  if (exclude.includes(testType)) {
    return false;
  }

  return true;
}

function NewCreativePreview() {
  // useHeader('Creatives Preview');
  const [query, setQuery] = useQuery();
  // Mosaic deep-links: mosaicRedirect=true plus alphCrid or alphCid (same gate as API/filters).
  const mosaicRedirect =
    query.get('mosaicRedirect') === 'true' &&
    (query.has('alphCrid') || query.has('alphCid'));
  const mosaicAlphCrid = query.get('alphCrid');
  const mosaicAlphCid = query.get('alphCid');

  const [expandAllArtifacts, setExpandAllArtifacts] = useState(false);
  const [expandedBatchIds, setExpandedBatchIds] = useState<
    Record<string, boolean>
  >({});
  const [mosaicAutoExpandEnabled, setMosaicAutoExpandEnabled] = useState(
    mosaicRedirect,
  );
  const [artifactsCollapseNonce, setArtifactsCollapseNonce] = useState(0);
  const [isChecked, setIsChecked] = useState(() => {
    if (mosaicRedirect) return false; //disable myCampaigns in the case of mosaic redirect
    const value = query.get('myCampaigns');
    return value === null || value === 'true';
  });
  const [isMyTestsChecked, setIsMyTestsChecked] = useState(() => {
    const value = query.get('myTests');
    return value === 'true' ? true : '';
  });
  const [itemsToShow, setItemsToShow] = useState(() =>
    query.get('limit') ? Number(query.get('limit')) : DefaultPageSize,
  );
  const [selectedTestType, setTestTypeState] = useState(
    query.get('testTag') ?? (mosaicRedirect ? 'ad-render' : 'cta'), // in case of mosaic redirect, default tab is ad-render
  );
  const [size, setPageSize] = useState(
    query.get('limit') ? Number(query.get('limit')) : DefaultPageSize,
  );
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [page, setPageNumber] = useState(
    query.get('page') ? Number(query.get('page')) : DefaultPage,
  );
  const { data: appConfig } = useGetConfigQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const mosaicUrl = appConfig?.mosaicUrl;
  const mosaicStagingUrl = appConfig?.mosaicStagingUrl;
  const {
    data: filters = [],
    isFetching,
    isLoading,
  } = useFetchMetadataQuery('filters');
  const visibleFilters = useMemo(() => {
    return filters.filter(filter =>
      isFilterVisibleForTestType(filter, selectedTestType),
    );
  }, [filters, selectedTestType]);
  const hiddenFilterKeysForSelectedTab = useMemo(
    () =>
      filters
        .filter(filter => !isFilterVisibleForTestType(filter, selectedTestType))
        .map(filter => filter?.key)
        .filter(Boolean),
    [filters, selectedTestType],
  );

  const noDataAnimationOptions = {
    loop: true,
    autoplay: true,
    animationData: noDataAnimation,
  };

  const isQueryReady = //mandatory params, hence waiting for them to compute before fetching data
    query.get('startDate') !== null &&
    query.get('endDate') !== null &&
    query.get('myCampaigns') !== null &&
    query.get('testTag') !== null &&
    query.get('limit') !== null &&
    query.get('page') !== null &&
    (!isInitialLoad ? true : query.get('status') !== null); // include status only on initial load

  useEffect(() => {
    if (isQueryReady) {
      setItemsToShow(size);
    }
  }, [query.toString(), isQueryReady]);

  useEffect(() => {
    track('adsure viewed', {
      adsure_test_tag: selectedTestType,
      my_campaigns: Boolean(isChecked),
      my_tests: Boolean(isMyTestsChecked),
      surface: 'adsure',
    });
  }, []);

  useEffect(() => {
    if (
      !selectedTestType ||
      (selectedTestType === 'on-demand' && query.get('myTests'))
    )
      return;

    const isOnDemand = selectedTestType === 'on-demand';
    const isProgrammatic = selectedTestType === 'ssp';
    const hiddenFilterReset = hiddenFilterKeysForSelectedTab.reduce(
      (acc, key) => ({
        ...acc,
        [key]: '',
      }),
      {},
    );

    setIsMyTestsChecked(isOnDemand);
    setQuery({
      myTests: isOnDemand ? 'true' : '',
      myCampaigns:
        mosaicRedirect || isProgrammatic ? 'false' : 'true',
      ...hiddenFilterReset,
      page: '1',
    });
    setIsChecked(!(mosaicRedirect || isProgrammatic));
  }, [hiddenFilterKeysForSelectedTab, mosaicRedirect, selectedTestType]);

  const queryString = query.toString().trim();

  const {
    data: dbDataWithCount,
    isLoading: isDataLoading,
    isFetching: isDataFetching,
    fulfilledTimeStamp,
    refetch,
    error,
  } = useFetchCreativeIdListQuery(
    isQueryReady ? { query: queryString } : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    if (!query.get('limit')) {
      setQuery('limit', DefaultPageSize.toString());
    } else setPageSize(Number(query.get('limit')));
    if (!query.get('page')) {
      setQuery('page', DefaultPage.toString());
    } else setPageNumber(Number(query.get('page')));
  }, [query]);

  useEffect(() => {
    if (!query.get('status')) {
      setQuery(
        'status',
        getDefaultStatusForTestType(selectedTestType, mosaicRedirect),
      );
    }
    if (!mosaicRedirect) setIsChecked(selectedTestType !== 'ssp');
    if (mosaicRedirect) setQuery('myCampaigns', 'false');
    // for mosaic redirect myCampaigns will remain false
    else setQuery('myCampaigns', selectedTestType === 'ssp' ? 'false' : 'true'); // everytime user refreshes the pages myCampaigns will be made true
    setIsInitialLoad(false);
  }, []); // only on first load

  useEffect(() => {
    setMosaicAutoExpandEnabled(mosaicRedirect);
  }, [mosaicRedirect, mosaicAlphCrid, mosaicAlphCid]);

  useEffect(() => {
    const hiddenFilterReset = hiddenFilterKeysForSelectedTab.reduce(
      (acc, key) => ({
        ...acc,
        [key]: '',
      }),
      {},
    );

    setQuery({
      testTag: selectedTestType,
      status: getDefaultStatusForTestType(selectedTestType, mosaicRedirect),
      ...hiddenFilterReset,
      page: '1',
    });
  }, [hiddenFilterKeysForSelectedTab, mosaicRedirect, selectedTestType]);

  const fetchMoreData = () => {
    setItemsToShow(prev => prev + size);
  };

  const showCollapseAll =
    expandAllArtifacts || (mosaicRedirect && mosaicAutoExpandEnabled);

  const previewListItems = useMemo(() => {
    const items: PreviewListItem[] = [];
    const groupsByParentId: Record<string, BatchListGroup> = {};
    const rows = dbDataWithCount?.data || [];

    rows.forEach((row, index) => {
      const parentTestId = getBatchParentId(row);
      const caseId = getBatchCaseId(row);
      const isBatchChild =
        selectedTestType === 'on-demand' && parentTestId && caseId;

      if (!isBatchChild) {
        items.push({ type: 'test', item: row, index });
        return;
      }

      if (!groupsByParentId[parentTestId]) {
        const summaryChildren = Array.isArray(row?.batchSummaryChildren)
          ? row.batchSummaryChildren
          : undefined;
        groupsByParentId[parentTestId] = {
          parentTestId,
          label: getBatchLabel(row, parentTestId),
          children: [],
          summaryChildren,
        };
        items.push({
          type: 'batch',
          group: groupsByParentId[parentTestId],
          index,
        });
      } else if (
        !groupsByParentId[parentTestId].summaryChildren?.length &&
        Array.isArray(row?.batchSummaryChildren)
      ) {
        groupsByParentId[parentTestId].summaryChildren =
          row.batchSummaryChildren;
      }

      groupsByParentId[parentTestId].children.push(row);
    });

    return items;
  }, [dbDataWithCount?.data, selectedTestType]);

  const toggleBatchExpanded = (parentTestId: string) => {
    setExpandedBatchIds(prev => ({
      ...prev,
      [parentTestId]: !prev[parentTestId],
    }));
  };

  const isAuthError =
    error &&
    'status' in error &&
    (error.status === 401 || error.status === 403);

  useEffect(() => {
    if (!dbDataWithCount) return;
    track('adsure results loaded', {
      adsure_test_tag: selectedTestType,
      result_count: dbDataWithCount?.data?.length || 0,
      total_count: dbDataWithCount?.total || 0,
      page,
      limit: size,
      my_campaigns: query.get('myCampaigns') === 'true',
      my_tests: query.get('myTests') === 'true',
      surface: 'adsure',
    });
  }, [dbDataWithCount, page, query, selectedTestType, size]);

  if (isAuthError && !isDataLoading && !isDataFetching) {
    return <Forbidden403 />;
  }

  return (
    <MainBodyStyle>
      <div className="header">
        <Header />
      </div>
      <div className="container-fluid text-center control-panel">
        <div className="row first-row w-100">
          <div className="col-2">
            <DropDownComponent
              isChecked={isChecked}
              setIsChecked={setIsChecked}
              selectedTestType={selectedTestType}
              isMyTestsChecked={isMyTestsChecked}
              setIsMyTestsChecked={setIsMyTestsChecked}
            />
          </div>
          <div className="col-2">
            <SearchComponent />
          </div>
          <div className="col-2">
            <DatePickerComponent mosaicRedirect={mosaicRedirect} />
          </div>
          <div className="col-6 d-flex">
            <ModalComponent filters={visibleFilters} isFetching={isFetching} />
            <OnDemandButton />
            <div style={{ width: '100%' }}>
              <LastFetched
                refetch={refetch}
                lastFetchedTime={fulfilledTimeStamp}
                isFetching={isDataFetching}
              />
            </div>
          </div>
        </div>
        <div className="row third-row w-100">
          <div className="col-10">
            <HomeBadgeComponent
              filters={visibleFilters}
              isFetching={isFetching}
              isLoading={isLoading}
              isChecked={isChecked}
              setIsChecked={setIsChecked}
              isMyTestsChecked={isMyTestsChecked}
              setIsMyTestsChecked={setIsMyTestsChecked}
            />
          </div>
        </div>
      </div>
      <div className="row fourth-row">
        <div className="d-flex justify-content-between align-items-center w-100 px-3">
          <div className="col-auto">
            <TestTypeTabs
              selectedTestType={selectedTestType}
              setTestTypeState={setTestTypeState}
            />
          </div>
          <div className="col-auto d-flex align-items-center gap-2">
            <Button
              renderVariant="outlined"
              onClick={() => {
                if (showCollapseAll) {
                  setExpandAllArtifacts(false);
                  setMosaicAutoExpandEnabled(false);
                  setArtifactsCollapseNonce(n => n + 1);
                } else {
                  setExpandAllArtifacts(true);
                }
              }}
              style={{ whiteSpace: 'nowrap' }}
            >
              {showCollapseAll ? 'Collapse all results' : 'Expand all results'}
            </Button>
            <PaginationComponent
              totalCount={dbDataWithCount?.total}
              size={size}
              setPageSize={setPageSize}
              page={page}
              setPageNumber={setPageNumber}
              isDataFetching={isDataFetching}
              isDataLoading={isDataLoading}
            />
          </div>
        </div>
      </div>
      <div className="row main-data-row">
        {isDataFetching || isDataLoading ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Spinners count={6} color={'#157f95'} />
          </div>
        ) : dbDataWithCount &&
          dbDataWithCount.data &&
          dbDataWithCount.data.length > 0 ? (
          <div id="scrollableDiv" style={{ height: '100%', overflow: 'auto' }}>
            <InfiniteScroll
              dataLength={itemsToShow}
              next={fetchMoreData}
              hasMore={itemsToShow < dbDataWithCount.data.slice(0, size).length}
              loader={<Spinners count={3} color={'#157f95'} />}
              scrollableTarget="scrollableDiv"
              endMessage={
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 10,
                  }}
                >
                  <PaginationComponent
                    totalCount={dbDataWithCount?.total}
                    size={size}
                    setPageSize={setPageSize}
                    page={page}
                    setPageNumber={setPageNumber}
                    isDataFetching={isDataFetching}
                    isDataLoading={isDataLoading}
                    showPage
                    showSize={false}
                    showTotal={false}
                  />
                </div>
              }
            >
              {previewListItems.map(displayItem => {
                if (displayItem.type === 'test') {
                  const { item, index } = displayItem;
                  return (
                    <Card
                      key={item?._id || index}
                      data={item}
                      testType={selectedTestType}
                      mosaicUrl={mosaicUrl}
                      mosaicStagingUrl={mosaicStagingUrl}
                      expandAllArtifacts={expandAllArtifacts}
                      artifactsCollapseNonce={artifactsCollapseNonce}
                      mosaicAutoExpand={
                        mosaicRedirect &&
                        mosaicAutoExpandEnabled &&
                        rowMatchesMosaicUrlIds(
                          item,
                          mosaicAlphCrid,
                          mosaicAlphCid,
                        )
                      }
                    />
                  );
                }

                const { group } = displayItem;
                const expanded = Boolean(expandedBatchIds[group.parentTestId]);

                return (
                  <div key={`batch-${group.parentTestId}`}>
                    <BatchSummaryCard
                      group={group}
                      expanded={expanded}
                      onToggle={() => toggleBatchExpanded(group.parentTestId)}
                    />
                    {expanded ? (
                      <div
                        style={{
                          margin: '0 0 12px 18px',
                          padding: '2px 0 2px 16px',
                          borderLeft: '4px solid #b8d4d9',
                          background:
                            'linear-gradient(90deg, #f8fbfc 0, rgba(248, 251, 252, 0) 65%)',
                        }}
                      >
                        {group.children.map((item, childIndex) => (
                          <Card
                            key={item?._id || `${group.parentTestId}-${childIndex}`}
                            data={item}
                            testType={selectedTestType}
                            mosaicUrl={mosaicUrl}
                            mosaicStagingUrl={mosaicStagingUrl}
                            expandAllArtifacts={expandAllArtifacts}
                            artifactsCollapseNonce={artifactsCollapseNonce}
                            mosaicAutoExpand={
                              mosaicRedirect &&
                              mosaicAutoExpandEnabled &&
                              rowMatchesMosaicUrlIds(
                                item,
                                mosaicAlphCrid,
                                mosaicAlphCid,
                              )
                            }
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </InfiniteScroll>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Lottie options={noDataAnimationOptions} height={300} width={300} />
            <p
              style={{
                fontSize: '1.2rem',
                color: '#999999',
                fontWeight: 'bold',
              }}
            >
              No Data Found
            </p>
          </div>
        )}
      </div>
    </MainBodyStyle>
  );
}
export default NewCreativePreview;
